import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useScreenshotMode } from "@/contexts/ScreenshotModeContext";

/**
 * When Screenshot Mode is on, strip `href` from in-app anchors so hovering
 * them does not pop the browser's status-bar URL (which would leak the
 * preview ID in screen recordings). Clicks still navigate via React Router.
 * External links (http/mailto/tel) and links opting out via
 * `data-keep-href` are left alone.
 */
const ATTR_STASH = "data-screenshot-href";

const isInternal = (href: string) =>
  href.startsWith("/") && !href.startsWith("//");

const ScreenshotLinkMasker = () => {
  const { enabled } = useScreenshotMode();
  const navigate = useNavigate();

  useEffect(() => {
    if (!enabled) return;

    const mask = (a: HTMLAnchorElement) => {
      if (a.dataset.keepHref === "true") return;
      const href = a.getAttribute("href");
      if (!href || !isInternal(href)) return;
      if (a.getAttribute(ATTR_STASH)) return;
      a.setAttribute(ATTR_STASH, href);
      a.removeAttribute("href");
      a.style.cursor = "pointer";
    };

    const unmask = (a: HTMLAnchorElement) => {
      const stashed = a.getAttribute(ATTR_STASH);
      if (!stashed) return;
      a.setAttribute("href", stashed);
      a.removeAttribute(ATTR_STASH);
      a.style.cursor = "";
    };

    const maskAll = (root: ParentNode) => {
      root.querySelectorAll<HTMLAnchorElement>("a[href]").forEach(mask);
    };

    const onClick = (e: MouseEvent) => {
      const target = (e.target as HTMLElement | null)?.closest("a");
      if (!target) return;
      const stashed = target.getAttribute(ATTR_STASH);
      if (!stashed) return;
      // Let modified clicks (new tab, download) fall through
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      e.preventDefault();
      navigate(stashed);
    };

    maskAll(document.body);
    document.addEventListener("click", onClick, true);

    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          const el = node as HTMLElement;
          if (el.tagName === "A") mask(el as HTMLAnchorElement);
          else if (el.querySelectorAll) maskAll(el);
        });
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      document.removeEventListener("click", onClick, true);
      document
        .querySelectorAll<HTMLAnchorElement>(`a[${ATTR_STASH}]`)
        .forEach(unmask);
    };
  }, [enabled, navigate]);

  return null;
};

export default ScreenshotLinkMasker;